/**
 * FilterBar.tsx
 * ----------------
 * Implementation of a filter bar.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import {
    Dropdown,
    IDropdownOption,
    IDropdownStyles,
} from '@fluentui/react/lib/Dropdown';
import { SearchBox } from '@fluentui/react/lib/SearchBox';
import * as React from 'react';

import { FluentSvgIcon } from './FluentSvgIcon';
import { StatusIndicator } from './StatusIndicator';
import { classNames } from '../../utilities/classNames';
import { useId } from '@fluentui/react-hooks/lib/useId';

/**
 * Component properties
 */

interface FilterBarProps {
    hasSearch?: boolean;
    isClosable?: boolean;
    onClose?: () => void;
    onSearchChanged?: (search: string) => void;
}

interface FilterProps {
    dropdownWidth?: number;
    label: string;
    options: FilterOptionProps[];
    placeholder: string;
}

interface FilterOptionProps {
    icon?: string;
    key: string;
    text: string;
}

interface MultiSelectFilterProps extends FilterProps {
    onChange: (selectedKeys: string[]) => void;
    selectedKeys: string[];
}

interface SingleSelectFilterProps extends FilterProps {
    onChange: (selectedKey: string) => void;
    selectedKey: string;
}

/**
 * Private functions
 */

function getFilterOptions(optionProps: FilterOptionProps[]): IDropdownOption[] {
    return optionProps.map((props) => {
        const iconProps =
            props.icon !== undefined
                ? { data: { icon: props.icon as string } }
                : {};

        return {
            key: props.key,
            text: props.text,
            ...iconProps,
        };
    });
}

function onRenderOption(option: IDropdownOption) {
    const { data, text } = option;

    return (
        <div className="filter-bar__filter-option">
            {data !== undefined && data.icon && data.icon === 'Advisory' && (
                <StatusIndicator
                    severity="Advisory"
                    state="Active"
                    svgStyles={{
                        className: 'filter-bar__filter-option-icon',
                    }}
                />
            )}

            {data !== undefined && data.icon && data.icon === 'Degraded' && (
                <StatusIndicator
                    severity="Degraded"
                    state="Active"
                    svgStyles={{
                        className: 'filter-bar__filter-option-icon',
                    }}
                />
            )}

            {data !== undefined && data.icon && data.icon === 'Unhealthy' && (
                <StatusIndicator
                    severity="Unhealthy"
                    state="Active"
                    svgStyles={{
                        className: 'filter-bar__filter-option-icon',
                    }}
                />
            )}

            {data !== undefined && data.icon && (
                <FluentSvgIcon
                    className="filter-bar__filter-option-icon"
                    iconName={data.icon}
                />
            )}

            <span>{text}</span>
        </div>
    );
}

/**
 * Implementation of a filter bar.
 * @param props Component properties
 * @returns Rendered content
 */
export function FilterBar(props: React.PropsWithChildren<FilterBarProps>) {
    const { children, hasSearch, isClosable, onClose, onSearchChanged } = props;
    const textFieldId = useId('searchInput');

    const filtersClasses = classNames(props)
        .add('filter-bar__filters')
        .add(
            'filter-bar__filters--no-search',
            (props) =>
                props.hasSearch === undefined || props.hasSearch === false
        );

    // Callback hooks
    const onCloseButtonClicked = React.useCallback(() => {
        if (onClose !== undefined) {
            onClose();
        }
    }, [onClose]);

    const onSearchSubmitted = React.useCallback(
        (value: string) => {
            if (onSearchChanged !== undefined) {
                onSearchChanged(value);
            }
        },
        [onSearchChanged]
    );

    // Validation
    if (isClosable === true && onClose === undefined) {
        console.warn(
            'Filter bar was marked as closable, but no onClose action was given.'
        );
    }

    if (hasSearch === true && onSearchChanged === undefined) {
        console.warn(
            'Filter bar has a search box, but no onSearchChanged action was given.'
        );
    }

    return (
        <div className="filter-bar">
            {hasSearch && (
                <SearchBox
                    id={textFieldId}
                    className="filter-bar__search"
                    iconProps={{ iconName: 'Filter' }}
                    onSearch={onSearchSubmitted}
                    placeholder="Search by event title"
                    ariaLabel="Search by event title"
                />
            )}

            <div className={filtersClasses.value()}>{children}</div>

            {isClosable && (
                <IconButton
                    ariaLabel="Close"
                    className="filter-bar__close-button"
                    iconProps={{ iconName: 'ChromeClose' }}
                    onClick={onCloseButtonClicked}
                    title="Close"
                />
            )}
        </div>
    );
}

/**
 * Dropdown filter allowing multiple selections.
 * @param props Component properties
 * @returns Rendered content
 */
export function MultiSelectFilter(props: MultiSelectFilterProps) {
    const {
        dropdownWidth,
        label,
        onChange,
        options,
        placeholder,
        selectedKeys,
    } = props;

    // Callback hooks
    const onSelectionsChanged = React.useCallback(
        (_event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
            if (item === undefined) {
                return;
            }

            const newSelectedKeys = item.selected
                ? [...selectedKeys, item.key.toString()]
                : selectedKeys.filter((key) => key !== item.key.toString());

            onChange(newSelectedKeys);
        },
        [onChange, selectedKeys]
    );

    const filterOptions = getFilterOptions(options);
    const filterTitle = (chosenOptions: IDropdownOption[]) => {
        const count = (chosenOptions ?? []).length;
        if (count === 0) {
            return null;
        }

        const firstText = chosenOptions[0].text;
        if (count === 1) {
            return <>{firstText}</>;
        }

        return (
            <>
                {firstText} (+{count - 1})
            </>
        );
    };

    return (
        <Dropdown
            calloutProps={{ className: 'filter-bar__dropdown-callout' }}
            className="filter-bar__select-filter"
            dropdownWidth={dropdownWidth}
            multiSelect
            onChange={onSelectionsChanged}
            onRenderOption={onRenderOption}
            options={filterOptions}
            placeholder={placeholder}
            selectedKeys={selectedKeys}
            title={label}
            onRenderTitle={filterTitle}
            ariaLabel={label}
            styles={{ root: { height: 'unset' } }}
        />
    );
}

/**
 * Dropdown filter allowing single selection.
 * @param props Component properties
 * @returns Rendered content
 */
export function SingleSelectFilter(props: SingleSelectFilterProps) {
    const {
        dropdownWidth,
        label,
        onChange,
        options,
        placeholder,
        selectedKey,
    } = props;

    const dropdownStyles: Partial<IDropdownStyles> = {
        dropdownItemSelected: {
            selectors: {
                '&:before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    background: 'rgb(0, 120, 212)',
                },
            },
        },
    };

    // Callback hooks
    const onSelectionChanged = React.useCallback(
        (_event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
            if (item === undefined) {
                return;
            }

            onChange(item.key.toString());
        },
        [onChange]
    );

    return (
        <Dropdown
            ariaLabel={label}
            calloutProps={{ className: 'filter-bar__dropdown-callout' }}
            className="filter-bar__select-filter"
            dropdownWidth={dropdownWidth}
            onChange={onSelectionChanged}
            onRenderOption={onRenderOption}
            options={getFilterOptions(options)}
            placeholder={placeholder}
            selectedKey={selectedKey}
            styles={dropdownStyles}
            title={label}
        />
    );
}
